import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  ArrowRight,
  Wrench,
  FileText,
  ShoppingCart,
  Lightbulb,
  CheckCircle2,
  ShieldCheck,
  Zap,
  Settings,
} from "lucide-react";
import { services, products as fallbackProducts, testimonials, stats, sectors } from "../mock";
import CTASection from "../components/CTASection";
import Coverage from "../components/Coverage";
import { getProducts } from "../lib/api";

const iconMap = { Wrench, FileText, ShoppingCart, Lightbulb };

const Home = () => {
  const [products, setProducts] = useState(fallbackProducts);

  useEffect(() => {
    getProducts()
      .then((res) => { if (res.data.length) setProducts(res.data); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="pointer-events-none absolute -left-24 top-10 h-80 w-80 rounded-full bg-blue-100/50 blur-3xl" />
        <div className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-blue-200/30 blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 py-16 md:px-8 md:py-24 lg:grid-cols-2">
          <div className="fade-up">
            <p className="eyebrow text-[#1d4ed8]">Window Controls &middot; Automation &middot; Electrical</p>
            <h1 className="heading-xl mt-4 text-4xl text-[#0f2a5a] md:text-5xl lg:text-6xl">
              Manual &amp; Electric <span className="text-[#1d4ed8]">Window Controls</span> for Natural Ventilation
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-gray-600 md:text-lg">
              L&amp;D Controls Ltd supplies and installs Teleflex manual winders, electric window
              motors and complete natural ventilation systems — nationwide, from a single
              residential opener to the largest commercial project.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2 rounded-md px-7 py-3.5 text-sm font-semibold">
                Get a Quote <ArrowRight size={17} />
              </Link>
              <Link to="/products" className="btn-ghost inline-flex items-center gap-2 rounded-md border border-[#0f2a5a]/20 px-7 py-3.5 text-sm font-semibold text-[#0f2a5a] hover:border-[#1d4ed8] hover:text-[#1d4ed8]">
                Explore Products
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
              {[
                [ShieldCheck, "Nationwide installation"],
                [Zap, "24V electric actuators"],
                [Settings, "Manual Teleflex winders"],
              ].map(([Icon, label]) => (
                <div key={label} className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Icon size={18} className="text-[#1d4ed8]" /> {label}
                </div>
              ))}
            </div>
          </div>

          <div className="fade-up relative">
            <div className="img-zoom overflow-hidden rounded-2xl border border-gray-100 shadow-2xl">
              <img
                src={products[0].image}
                alt="Window control installation"
                className="h-[360px] w-full object-cover md:h-[460px]"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden rounded-xl bg-white p-5 shadow-xl sm:block">
              <p className="font-head text-3xl font-800 text-[#0f2a5a]">1000+</p>
              <p className="text-sm text-gray-500">Windows automated</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-gray-100 bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-5 py-10 md:grid-cols-4 md:px-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-head text-3xl font-800 text-[#1d4ed8] md:text-4xl">{s.value}</p>
              <p className="mt-1 text-sm text-gray-500">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Intro */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-4xl px-5 text-center md:px-8">
          <p className="eyebrow text-[#1d4ed8]">Who We Are</p>
          <h2 className="heading-xl mt-3 text-3xl text-[#0f2a5a] md:text-4xl">
            Specialists in window control &amp; natural ventilation
          </h2>
          <div className="ld-accent-bar mx-auto mt-5" />
          <p className="mt-6 text-base leading-relaxed text-gray-600 md:text-lg">
            With extensive experience and first-hand product knowledge, L&amp;D Controls can undertake
            every project requirement — from a single remote window opener in a residential property
            to the largest school, healthcare and commercial projects. We combine reliable products
            with meticulous installation and genuine expert advice.
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="bg-gray-50 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="mb-12 text-center">
            <p className="eyebrow text-[#1d4ed8]">What We Do</p>
            <h2 className="heading-xl mt-3 text-3xl text-[#0f2a5a] md:text-4xl">Our Services</h2>
            <div className="ld-accent-bar mx-auto mt-5" />
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((s) => {
              const Icon = iconMap[s.icon];
              return (
                <div key={s.id} className="card-hover rounded-xl border border-gray-100 bg-white p-7">
                  <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-blue-50 text-[#1d4ed8]">
                    <Icon size={26} />
                  </div>
                  <h3 className="mt-5 font-head text-lg font-700 text-[#0f2a5a]">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-gray-600">{s.short}</p>
                  <Link to="/services" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#1d4ed8] hover:gap-2">
                    Learn more <ArrowRight size={15} />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products preview */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="mb-12 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <p className="eyebrow text-[#1d4ed8]">Our Range</p>
              <h2 className="heading-xl mt-3 text-3xl text-[#0f2a5a] md:text-4xl">Products &amp; Systems</h2>
              <div className="ld-accent-bar mt-5" />
            </div>
            <Link to="/products" className="inline-flex items-center gap-1 text-sm font-semibold text-[#1d4ed8] hover:gap-2">
              View all products <ArrowRight size={15} />
            </Link>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {products.slice(0, 3).map((p) => (
              <Link to="/products" key={p.id} className="card-hover group overflow-hidden rounded-xl border border-gray-100 bg-white">
                <div className="img-zoom h-56">
                  <img src={p.image} alt={p.name} className="h-full w-full object-cover" />
                </div>
                <div className="p-6">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#1d4ed8]">{p.category}</span>
                  <h3 className="mt-2 font-head text-lg font-700 text-[#0f2a5a]">{p.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">{p.summary}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-5xl px-5 text-center md:px-8">
          <p className="eyebrow text-[#1d4ed8]">Where We Work</p>
          <h2 className="heading-xl mt-3 text-3xl text-[#0f2a5a] md:text-4xl">Sectors We Serve</h2>
          <div className="ld-accent-bar mx-auto mt-5" />
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {sectors.map((s) => (
              <span key={s} className="flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-5 py-2.5 text-sm font-medium text-gray-700">
                <CheckCircle2 size={16} className="text-[#1d4ed8]" /> {s}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-gray-50 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="mb-12 text-center">
            <p className="eyebrow text-[#1d4ed8]">Testimonials</p>
            <h2 className="heading-xl mt-3 text-3xl text-[#0f2a5a] md:text-4xl">What Our Clients Say</h2>
            <div className="ld-accent-bar mx-auto mt-5" />
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {testimonials.map((t) => (
              <div key={t.name} className="rounded-xl border border-gray-100 bg-white p-7">
                <div className="flex gap-1 text-[#1d4ed8]">
                  {["s1", "s2", "s3", "s4", "s5"].map((s) => (
                    <span key={s} className="text-lg">★</span>
                  ))}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-gray-700">“{t.quote}”</p>
                <div className="mt-5 border-t border-gray-100 pt-4">
                  <p className="font-head text-sm font-700 text-[#0f2a5a]">{t.name}</p>
                  <p className="text-xs text-gray-500">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Coverage />

      <CTASection />
    </div>
  );
};

export default Home;